import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { noop, isEmpty, isEqual } from 'lodash-es';
import { Row, Col } from 'reactstrap';
/* Utils */
import { getFinancingContainerFields } from 'site-modules/financing/utils/financing-utils';
import { updateCreativeId } from 'site-modules/financing/utils/update-creative-id';
/* Constants */
import { PERSONAL_STEP } from 'site-modules/shared/constants/financing/steps';
/* Components */
import { PersonalScreenForm } from 'site-modules/financing/components/personal-screen/components/personal-screen-form/personal-screen-form';
/* hooks */
import { useFieldsControl } from 'client/site-modules/financing/hooks/use-fields-control';
import './personal-screen.scss';

export function PersonalScreen({
  isJoint = false,
  enableNextBtn = noop,
  enableUpdateBtn = noop,
  financingFields = {},
  isReview = false,
  validationInfo = {},
  skipDataTrackingParent = false,
  isBackState = false,
  prefillFromStorage = false, // TODO: Remove when shop-4713-prequal2 test is cleaned up
  isAllStepsSkipped = false,
  isMatchingVIN = false,
  isProdEnv = false,
  setFormData = noop,
  isPreVinFlow = false,
}) {
  const [primaryFields, setPrimaryFields] = useState({});
  const [coBorrowerFields, setCoBorrowerFields] = useState({});
  const [areSsnEqual, setAreSsnEqual] = useState(false);
  const {
    predefinedFields,
    formFields,
    isPrimaryFormValid,
    isCoborrowerFormValid,
    isFormReady,
    setIsFormReady,
    setFormFields,
    setIsPrimaryFormValid,
    setIsCoborrowerFormValid,
  } = useFieldsControl({
    financingFields,
    setFormData,
    enableNextBtn,
    isBackState,
    prefillFromStorage,
    getFinancingContainerFields,
    isJoint,
    step: PERSONAL_STEP,
  });

  useEffect(() => {
    const isFormValid = isJoint ? isPrimaryFormValid && isCoborrowerFormValid : isPrimaryFormValid;
    const currentFields = isJoint ? { ...primaryFields, ...coBorrowerFields } : primaryFields;

    if (!isEmpty(currentFields) && !isEqual(formFields, currentFields)) {
      setFormFields({ ...formFields, ...currentFields });
      setFormData({ ...formFields, ...currentFields });
      if (isReview) {
        enableUpdateBtn({ ...predefinedFields, ...currentFields }, isFormValid);
      } else {
        enableNextBtn(isFormValid);
      }
    }

    if (isAllStepsSkipped && isReview && isEmpty(currentFields)) {
      enableUpdateBtn({}, isFormValid);
    }

    enableNextBtn(isFormReady && isFormValid);

    // eslint-disable-next-line
  }, [coBorrowerFields, isCoborrowerFormValid, isJoint, isPrimaryFormValid, primaryFields, isFormReady]);

  return (
    <Row className="personal-screen mt-1_5">
      <Col className="screen-form mx-auto">
        <PersonalScreenForm
          headlineText="Primary Borrower"
          hideLegend={!isJoint}
          predefinedFields={predefinedFields}
          isPrimary
          isReview={isReview}
          setFields={setPrimaryFields}
          setIsValid={setIsPrimaryFormValid}
          validationInfo={validationInfo.primary}
          dataTrackingParent={
            skipDataTrackingParent
              ? null
              : updateCreativeId({ isPreVinFlow, isMatchingVIN, trackingParent: 'personal_individual' })
          }
          isAllStepsSkipped={isAllStepsSkipped}
          isProdEnv={isProdEnv}
          enableNextBtn={enableNextBtn}
          setIsFormReady={setIsFormReady}
          formFields={coBorrowerFields}
          areSsnEqual={areSsnEqual}
          setAreSsnEqual={setAreSsnEqual}
        />
        <hr className={classnames('mt-2_5', { 'mb-1': !isJoint, 'mb-1_5': isJoint })} />
        {isJoint && (
          <>
            <PersonalScreenForm
              headlineText="Co-Borrower"
              predefinedFields={predefinedFields}
              setFields={setCoBorrowerFields}
              setIsValid={setIsCoborrowerFormValid}
              isReview={isReview}
              validationInfo={validationInfo.joint}
              dataTrackingParent={
                skipDataTrackingParent
                  ? null
                  : updateCreativeId({ isPreVinFlow, isMatchingVIN, trackingParent: 'personal_joint' })
              }
              isAllStepsSkipped={isAllStepsSkipped}
              isProdEnv={isProdEnv}
              enableNextBtn={enableNextBtn}
              setIsFormReady={setIsFormReady}
              isJoint={isJoint}
              formFields={primaryFields}
              areSsnEqual={areSsnEqual}
              setAreSsnEqual={setAreSsnEqual}
            />
            <hr className="mt-2_5 mb-1" />
          </>
        )}
      </Col>
    </Row>
  );
}

PersonalScreen.propTypes = {
  isJoint: PropTypes.bool,
  enableNextBtn: PropTypes.func,
  enableUpdateBtn: PropTypes.func,
  financingFields: PropTypes.shape({}),
  isReview: PropTypes.bool,
  validationInfo: PropTypes.shape({}),
  skipDataTrackingParent: PropTypes.bool,
  isBackState: PropTypes.bool,
  prefillFromStorage: PropTypes.bool,
  isAllStepsSkipped: PropTypes.bool,
  isMatchingVIN: PropTypes.bool,
  isProdEnv: PropTypes.bool,
  setFormData: PropTypes.func,
  isPreVinFlow: PropTypes.bool,
};
